import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, MessageSquare, Wallet, ArrowLeftRight, Target, Sparkles, Settings, LogOut, Calculator, Gift, Users, ScrollText, PieChart, Heart, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import AskCoachButton from "@/components/AskCoachButton";
import AnimatedLogo from "@/components/AnimatedLogo";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/coach", label: "AI Coach", icon: MessageSquare, id: "nav-coach" },
  { to: "/accounts", label: "Accounts", icon: Wallet, id: "nav-accounts" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, id: "nav-transactions" },
  { to: "/budgets", label: "Budgets", icon: PieChart, id: "nav-budgets" },
  { to: "/goals", label: "Goals", icon: Target, id: "nav-goals" },
  { to: "/debt-simulator", label: "Debt Simulator", icon: Calculator, id: "nav-debt-simulator" },
  { to: "/challenges", label: "Challenges", icon: Users, id: "nav-challenges" },
  { to: "/family", label: "Family", icon: Heart, id: "nav-family" },
  { to: "/insights", label: "Insights", icon: Sparkles, id: "nav-insights" },
  { to: "/recap", label: "Weekly Recap", icon: ScrollText, id: "nav-recap" },
  { to: "/referrals", label: "Referrals", icon: Gift, id: "nav-referrals" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/admin/me").then(r => setIsAdmin(r.data.is_admin)).catch(() => setIsAdmin(false));
  }, [user?.email]);

  const navItems = isAdmin ? [...nav, { to: "/admin", label: "Admin", icon: Shield, id: "nav-admin" }] : nav;

  return (
    <div className="flex min-h-screen">
      <aside className="w-64 shrink-0 border-r border-[rgba(212,175,55,0.12)] bg-[color:var(--bg-card)] p-6 flex flex-col">
        <Link to="/dashboard" data-testid="app-logo" className="flex items-center gap-2.5 mb-10 group">
          <AnimatedLogo size={40} className="shrink-0" />
          <span className="font-heading text-xl font-bold gold-gradient-text tracking-tight">AfroRichMind</span>
        </Link>
        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map(({ to, label, icon: Icon, id }) => {
            const active = location.pathname === to;
            return (
              <Link key={to} to={to} data-testid={id}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? "bg-[rgba(212,175,55,0.12)] text-[color:var(--gold)]" : "text-[color:var(--text-secondary)] hover:bg-[rgba(212,175,55,0.05)] hover:text-[color:var(--text-primary)]"}`}>
                <Icon className="w-4 h-4" /> <span className="text-sm font-medium">{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-[rgba(212,175,55,0.1)] pt-4">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[color:var(--gold)] font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[color:var(--text-muted)] capitalize">{user?.tier || "free"}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-[color:var(--text-secondary)] hover:bg-[rgba(224,106,59,0.1)] hover:text-[color:var(--terracotta)] transition">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-40 backdrop-blur-xl bg-[rgba(10,11,14,0.75)] border-b border-[rgba(212,175,55,0.08)] px-8 py-3 flex justify-end">
          <NotificationBell />
        </div>
        {children}
        <AskCoachButton />
      </main>
    </div>
  );
}
